/** packages/shared-components/src/components/Keyboard/KeyboardSkeleton.tsx */

import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardType, KeyStyleMode } from "./types";
import { generateDefaultLayout } from "../../utils/keyboardLayoutUtils";
import { usesSliceFirstRowLayout } from "../../utils/sliceFirstRowKeyboardMatrix";
import { isGap } from "./utils";
import "./KeyboardSkeleton.css";
import "./Keyboard.css";

const CONTAINER_PADDING = 6;

export interface KeyboardSkeletonProps {
  keyboardType?: KeyboardType;
  keySize?: number;
  keyGap?: number;
  styleMode?: KeyStyleMode;
  className?: string;
}

export const KeyboardSkeleton: React.FC<KeyboardSkeletonProps> = ({
  keyboardType = "87",
  keySize = 54,
  keyGap = 4,
  styleMode = "colorful",
  className = "",
}) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const skeletonLayout = useMemo(() => {
    let rows = generateDefaultLayout(keyboardType);
    if (usesSliceFirstRowLayout(keyboardType)) {
      rows = rows.slice(1);
    }
    return rows;
  }, [keyboardType]);

  useLayoutEffect(() => {
    if (!shellRef.current) return;

    const measure = () => {
      if (!shellRef.current) return;
      const rows = shellRef.current.querySelectorAll(".shared-keyboard-row");
      let maxWidth = 0;
      rows.forEach((row) => {
        maxWidth = Math.max(maxWidth, row.scrollWidth);
      });
      const actualHeight = shellRef.current.scrollHeight;
      setDimensions({
        width: Math.round(maxWidth + CONTAINER_PADDING * 2),
        height: Math.round(actualHeight + CONTAINER_PADDING * 2),
      });
    };

    const t = setTimeout(measure, 0);
    return () => clearTimeout(t);
  }, [skeletonLayout.length, keySize, keyGap, keyboardType]);

  const modeClass =
    styleMode === "neutral"
      ? "keyboard-skeleton--neutral"
      : "keyboard-skeleton--colorful";

  return (
    <div
      ref={shellRef}
      className={`keyboard-skeleton shared-keyboard shared-keyboard-type-${keyboardType} shared-keyboard-style-${styleMode} ${modeClass} ${className}`.trim()}
      style={
        {
          width: dimensions.width > 0 ? `${dimensions.width}px` : "auto",
          height: `${dimensions.height}px`,
          "--key-size": `${keySize}px`,
          "--key-gap": `${keyGap}px`,
          borderRadius: "12px",
        } as React.CSSProperties
      }
      aria-busy="true"
      aria-label="Keyboard loading"
    >
      <div className="keyboard-skeleton__keys-layer">
        {skeletonLayout.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={`keyboard-skeleton__row shared-keyboard-row`}
          >
            {row.map((key, keyIndex) => {
              if (isGap(key)) return null;

              let totalWidth = 0;
              for (let i = 0; i < keyIndex; i++) {
                const prev = row[i];
                if (prev) {
                  totalWidth += prev.multipleWidth * keySize;
                  if (!isGap(prev)) {
                    totalWidth += keyGap;
                  }
                }
              }

              const w = key.multipleWidth * keySize;
              const h = keySize;

              return (
                <div
                  key={`${rowIndex}-${keyIndex}`}
                  className="keyboard-skeleton__cell"
                  style={{
                    left: `${totalWidth + CONTAINER_PADDING}px`,
                    top: `${rowIndex * (keySize + keyGap) + CONTAINER_PADDING}px`,
                    width: `${w}px`,
                    height: `${h}px`,
                  }}
                >
                  <div className="keyboard-skeleton__cell-inner" />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
